import React, { useEffect, useMemo, useState } from 'react';
import {
    Continued,
    CTableWrapper,
    DBlock,
    DCheckbox,
    DeleteIcon,
    DIdAutocomplete,
    Discontinued,
    DPage,
    DPageBody,
    DPaginator,
    DTextField,
    EditIcon,
    SortedOrder,
    usePaginator,
    ViewIcon,
} from '../../../../dum';
import { SDeleteProductDialog, SUpsertProductDialog } from '../../../../smart';
import {
    ColumnOrder,
    navigateToProductPage,
    ProductsPageQuery,
    ProductsSortableFields,
    useProductsPageLazyQuery,
} from '../../../../../services';
import { Box, IconButton, Typography } from '@mui/material';
import {
    COLUMN_LABELS,
    FIELD_LABELS,
    formatRelativeDate,
    shortLabel,
    useBoolean,
    useDebounce,
    useNumber,
    useString,
} from '../../../../../helpers';
import { useNavigate } from 'react-router-dom';

export function ProductsPage() {
    const [query, { data, previousData, loading }] =
        useProductsPageLazyQuery();
    const navigate = useNavigate();
    const [upsertOpen, setUpsertOpen] = useState(false);
    const [deleteOpen, setDeleteOPen] = useState(false);
    const [productId, setProductId] = useState<number | null>(null);
    const [sortedIndex, setSortedIndex] = useState<number | null>(null);
    const [sortedOrder, setSortedOrder] = useState<SortedOrder>(null);

    const [includeDiscontinued, setIncludeDiscontinued] = useBoolean({
        KEY: ProductsPage.name + '_IncludeDiscontinued',
        defaultValue: true,
    });

    const [onlyMissingInternal, setOnlyMissingInternal] = useBoolean({
        KEY: ProductsPage.name + '_OnlyMissingInternal',
        defaultValue: false,
    });

    const [productCategoryId, setProductCategoryId] = useNumber({
        KEY: ProductsPage.name + '_ProductCategoryId',
        defaultValue: null,
    });

    const [filter, setFilter] = useString({
        KEY: ProductsPage.name + '_Filter',
    });

    const debouncedFilter = useDebounce(filter, 400);

    const { setSkip, skip, take } = usePaginator(
        {
            KEY: ProductsPage.name + '_Paginator',
        },
        [
            debouncedFilter,
            includeDiscontinued,
            onlyMissingInternal,
            productCategoryId,
        ],
    );

    useEffect(() => {
        void query({
            variables: {
                skip: skip,
                take: take,
                filter: debouncedFilter,
                productCategoryId: productCategoryId,
                includeDiscontinued: includeDiscontinued,
                onlyMissingInternal: onlyMissingInternal,
                sortField:
                    sortedIndex === 1
                        ? ProductsSortableFields.ExternalDescription
                        : sortedIndex === 2
                        ? ProductsSortableFields.InternalDescription
                        : sortedIndex === 3
                        ? ProductsSortableFields.OrderProductionTypeId
                        : sortedIndex === 4
                        ? ProductsSortableFields.ProductCategoryId
                        : null,
                sortOrder:
                    sortedOrder === 'asc'
                        ? ColumnOrder.Asc
                        : sortedOrder === 'desc'
                        ? ColumnOrder.Desc
                        : null,
            },
        });
    }, [
        query,
        skip,
        take,
        debouncedFilter,
        productCategoryId,
        includeDiscontinued,
        onlyMissingInternal,
        sortedOrder,
        sortedIndex,
    ]);

    const products =
        data?.paginatedProducts.docs ||
        previousData?.paginatedProducts.docs ||
        [];

    const count =
        data && data?.paginatedProducts.count >= 0
            ? data?.paginatedProducts.count
            : previousData?.paginatedProducts.count;

    const handleEditProduct = useMemo(() => {
        return (
            product: ProductsPageQuery['paginatedProducts']['docs'][number],
        ) => {
            setProductId(product.id);
            setUpsertOpen(true);
        };
    }, []);

    const handleDeleteProduct = useMemo(() => {
        return (
            product: ProductsPageQuery['paginatedProducts']['docs'][number],
        ) => {
            setProductId(product.id);
            setDeleteOPen(true);
        };
    }, []);

    const handleViewProduct = useMemo(() => {
        return (
            product: ProductsPageQuery['paginatedProducts']['docs'][number],
        ) => {
            navigateToProductPage({
                navigate,
                productId: product.id,
            });
        };
    }, [navigate]);

    return (
        <DPage>
            <DPageBody disableGutters>
                <DBlock>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Productos</Typography>
                    <Box sx={{ display: 'flex', mb: 2, alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <DTextField
                                label={'Filtro'}
                                helperText={'Descripción interna o externa del producto.'}
                                value={filter}
                                onChange={(value) => setFilter(value || '')}
                                hideHelperTextBelowLg
                            />
                        </Box>
                        <DPaginator skip={skip} take={take} setSkip={setSkip} count={count} disableGutters />
                    </Box>
                    <CTableWrapper
                        onRowDoubleClick={(product) => {
                            handleEditProduct(product);
                        }}
                        sortedIndex={sortedIndex}
                        setSortedIndex={setSortedIndex}
                        sortedOrder={sortedOrder}
                        setSortedOrder={setSortedOrder}
                        contextItems={[
                            {
                                title: shortLabel(COLUMN_LABELS.view),
                                callback: (product) => {
                                    handleViewProduct(product);
                                },
                            },
                            {
                                title: shortLabel(COLUMN_LABELS.edit),
                                callback: (product) => {
                                    handleEditProduct(product);
                                },
                            },
                            {
                                title: shortLabel(COLUMN_LABELS.delete),
                                disable: (product) => {
                                    return !product.is_deletable;
                                },
                                callback: (product) => {
                                    handleDeleteProduct(product);
                                },
                            },
                        ]}
                        items={[
                            {
                                title: shortLabel(FIELD_LABELS.updated_at),
                                cellGrow: 20,
                                textAlign: 'center',
                                content: (product) => {
                                    return formatRelativeDate(
                                        product.updated_at,
                                    );
                                },
                            },
                            {
                                title: shortLabel(
                                    FIELD_LABELS.external_description,
                                ),
                                cellGrow: 20,
                                content: (product) => {
                                    return product.external_description;
                                },
                                sortable: true,
                            },
                            {
                                title: shortLabel(
                                    FIELD_LABELS.internal_description,
                                ),
                                cellGrow: 20,
                                sortable: true,
                                filter: (
                                    <Box sx={{ width: '100%' }}>
                                        <DCheckbox
                                            align={'left'}
                                            disableGutters
                                            onChange={setOnlyMissingInternal}
                                            value={onlyMissingInternal}
                                            label={'Sin descripción interna'}
                                        />
                                    </Box>
                                ),
                                content: (product) => {
                                    return product.internal_description;
                                },
                            },
                            {
                                title: shortLabel(
                                    FIELD_LABELS.order_production_type_id,
                                ),
                                cellGrow: 20,
                                content: (product) => {
                                    return product.order_production_type?.name;
                                },
                                sortable: true,
                            },
                            {
                                title: shortLabel(
                                    FIELD_LABELS.product_category_id,
                                ),
                                cellGrow: 20,
                                sortable: true,
                                filter: (
                                    <Box sx={{ width: '100%', '& .MuiTextField-root': { minWidth: 0 } }}>
                                        <DIdAutocomplete
                                            id={productCategoryId}
                                            onChange={setProductCategoryId}
                                            itemText={'name'}
                                            label={'Categoría'}
                                            options={data?.getProductCategories || []}
                                            disableGutters
                                        />
                                    </Box>
                                ),
                                content: (product) => {
                                    return product.product_category?.name;
                                },
                            },
                            {
                                title: shortLabel(
                                    FIELD_LABELS.product_material_id,
                                ),
                                cellGrow: 20,
                                content: (product) => {
                                    return product.product_material?.name;
                                },
                            },
                            {
                                title: shortLabel(FIELD_LABELS.discontinued),
                                textAlign: 'center',
                                filter: (
                                    <Box sx={{ width: '100%' }}>
                                        <DCheckbox
                                            align={'left'}
                                            disableGutters
                                            onChange={setIncludeDiscontinued}
                                            value={includeDiscontinued}
                                            label={'Descontinuados'}
                                        />
                                    </Box>
                                ),
                                content: (product) => {
                                    return !product.discontinued ? (
                                        <Continued color={'success'} />
                                    ) : (
                                        <Discontinued color={'error'} />
                                    );
                                },
                            },
                            {
                                title: shortLabel(FIELD_LABELS.updated_by_id),
                                cellGrow: 20,
                                content: (product) => {
                                    return product.updated_by?.fullname || '—';
                                },
                            },
                            {
                                title: '',
                                textAlign: 'center',
                                content: (product) => {
                                    return (
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <IconButton
                                                size="small"
                                                sx={{ bgcolor: 'background.paper', boxShadow: 2, borderRadius: '50%' }}
                                                onClick={() => {
                                                    handleViewProduct(product);
                                                }}
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                sx={{ bgcolor: 'rgba(34,197,94,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                                onClick={() => {
                                                    handleEditProduct(product);
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                sx={{ bgcolor: 'rgba(239,68,68,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                                disabled={!product.is_deletable}
                                                onClick={() => {
                                                    handleDeleteProduct(
                                                        product,
                                                    );
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    );
                                },
                            },
                        ]}
                        data={products}
                        loading={loading}
                    />
                </DBlock>
            </DPageBody>
            <SUpsertProductDialog
                open={upsertOpen}
                setOpen={setUpsertOpen}
                productId={productId}
            />
            <SDeleteProductDialog
                open={deleteOpen}
                setOpen={setDeleteOPen}
                productId={productId}
            />
        </DPage>
    );
}
